import { type ComponentType, type ReactNode } from 'react'
import { Inbox, type LucideProps } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'

/**
 * AF-S29.6 — Empty state primitive.
 *
 * Used wherever a list / table / panel renders zero rows. Replaces the
 * ad-hoc "No <thing> found" paragraphs scattered across feature pages
 * with a consistent shape: icon, title, optional description, and up
 * to two calls-to-action.
 *
 * Variants:
 *   - default — full panel with icon + title + description + actions.
 *               Use when the empty cell is the main content of a Card
 *               or the whole route.
 *   - compact — inline single-line variant for narrow containers
 *               (table cells, side panels, filter results inside an
 *               already-titled Card).
 *
 * Why a primitive instead of inline JSX: the design-system tokens for
 * muted-foreground / border / spacing should live in one place so a
 * future redesign sweep is one file, not 30. It also keeps the
 * keyboard / aria semantics consistent — actions are real `<button>`
 * elements, not links-styled-as-buttons that screen readers ignore.
 */

type Icon = ComponentType<LucideProps>

interface EmptyStateAction {
  label: string
  onClick: () => void
  icon?: Icon
  /** When true, render this action as `disabled`. Useful when the
   *  caller knows the user lacks the permission for the action and
   *  wants to surface it visibly rather than hide it. */
  disabled?: boolean
  /** Tooltip — typically used to explain why a disabled action is
   *  disabled (e.g. "Requires databus:admin"). */
  title?: string
}

export interface EmptyStateProps {
  /** Short title — what's missing. e.g. "No cases yet". */
  title: string
  /** One-line explanation. Optional — not every empty state needs prose. */
  description?: ReactNode
  /** Icon component (lucide). Defaults to `Inbox`. */
  icon?: Icon
  /** Primary call-to-action — typically the "create" path. */
  action?: EmptyStateAction
  /** Secondary action — typically a "clear filters" / "reset" path. */
  secondaryAction?: EmptyStateAction
  /** Compact single-line variant for narrow containers. */
  compact?: boolean
  className?: string
}

export function EmptyState({
  title,
  description,
  icon: Icon = Inbox,
  action,
  secondaryAction,
  compact = false,
  className,
}: EmptyStateProps) {
  if (compact) {
    return (
      <div
        role="status"
        className={cn(
          'flex items-center gap-3 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground',
          className,
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="flex-1">
          <span className="font-medium text-fg">{title}</span>
          {description ? <span className="ml-2">{description}</span> : null}
        </div>
        {action ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={action.onClick}
            disabled={action.disabled}
            title={action.title}
          >
            {action.icon ? <action.icon className="mr-1 h-3 w-3" aria-hidden="true" /> : null}
            {action.label}
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-10 text-center',
        className,
      )}
    >
      <div
        className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground"
        aria-hidden="true"
      >
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="text-base font-semibold text-fg">{title}</h3>
      {description ? (
        <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
      ) : null}
      {(action || secondaryAction) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {action ? (
            <Button
              onClick={action.onClick}
              disabled={action.disabled}
              title={action.title}
            >
              {action.icon ? <action.icon className="mr-2 h-4 w-4" aria-hidden="true" /> : null}
              {action.label}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button
              variant="ghost"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
              title={secondaryAction.title}
            >
              {secondaryAction.icon ? (
                <secondaryAction.icon className="mr-2 h-4 w-4" aria-hidden="true" />
              ) : null}
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  )
}
